import React, { createContext, useContext, useState, useEffect } from "react";
import { AuthContext } from "../Auth/AuthContext";

const ValidationProgressContext = createContext();

export const useValidationProgress = () => {
  const context = useContext(ValidationProgressContext);
  if (!context) {
    throw new Error("useValidationProgress must be used within a ValidationProgressProvider");
  }
  return context;
};

export const ValidationProgressProvider = ({ children }) => {
  const { socket, user } = useContext(AuthContext);
  const [validationProgress, setValidationProgress] = useState({
    isValidating: false,
    processed: 0,
    total: 0,
    message: "",
    contactListId: null
  });

  useEffect(() => {
    if (!socket || !user?.companyId) return;

    // Listener para progresso de validação
    const onValidationProgress = (data) => {
      setValidationProgress({
        isValidating: true,
        processed: data.processed || 0,
        total: data.total || 0,
        message: data.message || "Validando contatos...",
        contactListId: data.contactListId || null
      });
    };

    // Listener para finalização da validação
    const onValidationCompleted = (data) => {
      setValidationProgress({
        isValidating: false,
        processed: data.processed || 0,
        total: data.total || 0,
        message: data.message || "Validação concluída!",
        contactListId: data.contactListId || null
      });

      // Limpar o progresso após 3 segundos
      setTimeout(() => {
        setValidationProgress({
          isValidating: false,
          processed: 0,
          total: 0,
          message: "",
          contactListId: null
        });
      }, 3000);
    };

    // Registrar listeners globais para validação
    socket.on(`company-${user.companyId}-validation-progress`, onValidationProgress);
    socket.on(`company-${user.companyId}-validation-completed`, onValidationCompleted);

    return () => {
      socket.off(`company-${user.companyId}-validation-progress`, onValidationProgress);
      socket.off(`company-${user.companyId}-validation-completed`, onValidationCompleted);
    };
  }, [socket, user?.companyId]);

  const startValidation = (contactListId, total = 0) => {
    setValidationProgress({
      isValidating: true,
      processed: 0,
      total: total,
      message: "Iniciando validação...",
      contactListId: contactListId
    });
  };

  const updateValidationProgress = (processed, total) => {
    setValidationProgress(prev => ({
      ...prev,
      processed: processed,
      total: total,
      message: `Validando contatos... ${processed}/${total}`
    }));
  };

  const finishValidation = () => {
    setValidationProgress(prev => ({
      ...prev,
      isValidating: false,
      message: "Validação concluída!"
    }));

    // Limpar o progresso após 3 segundos
    setTimeout(() => {
      clearValidationProgress();
    }, 3000);
  };

  const clearValidationProgress = () => {
    setValidationProgress({
      isValidating: false,
      processed: 0,
      total: 0,
      message: "",
      contactListId: null
    });
  };

  return (
    <ValidationProgressContext.Provider
      value={{
        validationProgress,
        setValidationProgress,
        startValidation,
        updateValidationProgress,
        finishValidation,
        clearValidationProgress
      }}
    >
      {children}
    </ValidationProgressContext.Provider>
  );
};